/**
 * The checks an agent's answer goes through before it is sent (spec
 * 2026-10-20-12). They mirror the server's (internal/handlers/campaigns
 * ValidateAnswer): the server refuses the same values, this only says it
 * sooner and in the agent's language.
 */

export interface AnswerSpec {
    data_type: string
    options?: { value: string }[]
    multiple?: boolean
    min?: number | null
    max?: number | null
    integer_only?: boolean
}

/** Why a value is refused, as an i18n key of the `campaigns` namespace with its values. */
export interface AnswerError {
    key: string
    values?: Record<string, number | string>
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/

function validDate(s: string): boolean {
    const m = DATE.exec(s)
    if (!m) return false
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3])
}

function checkScalar(spec: AnswerSpec, value: unknown): AnswerError | null {
    switch (spec.data_type) {
        case 'text':
            return typeof value === 'string' ? null : { key: 'answer_error.text' }
        case 'number': {
            if (typeof value !== 'number' || Number.isNaN(value)) return { key: 'answer_error.number' }
            if (spec.integer_only && !Number.isInteger(value)) return { key: 'answer_error.integer' }
            if (spec.min != null && value < spec.min) return { key: 'answer_error.min', values: { min: spec.min } }
            if (spec.max != null && value > spec.max) return { key: 'answer_error.max', values: { max: spec.max } }
            return null
        }
        case 'date':
            return typeof value === 'string' && validDate(value) ? null : { key: 'answer_error.date' }
        case 'boolean':
            return typeof value === 'boolean' ? null : { key: 'answer_error.boolean' }
        case 'enum':
            return typeof value === 'string' && (spec.options ?? []).some((o) => o.value === value)
                ? null
                : { key: 'answer_error.enum' }
        default:
            return null
    }
}

/** validateAnswer: null when the value may be sent. `null` (no answer) always may. */
export function validateAnswer(spec: AnswerSpec, value: unknown): AnswerError | null {
    if (value === null || value === undefined) return null
    if (spec.multiple && Array.isArray(value)) {
        for (const v of value) {
            const err = checkScalar(spec, v)
            if (err) return err
        }
        return null
    }
    return checkScalar(spec, value)
}

/**
 * parseInput turns what the agent typed into the value to send: a number
 * field gives a number (a comma is a decimal point), an empty field gives
 * null. A text that is not a number stays a string so validateAnswer says so.
 */
export function parseInput(dataType: string, raw: string): unknown {
    const s = raw.trim()
    if (s === '') return null
    if (dataType === 'number') {
        const n = Number(s.replace(',', '.').replace(/\s/g, ''))
        return Number.isNaN(n) ? s : n
    }
    return s
}

/** sameValue compares two answers by meaning (3 and 3.0, a missing value and null). */
export function sameValue(a: unknown, b: unknown): boolean {
    if (a === null || a === undefined || b === null || b === undefined) return (a ?? null) === (b ?? null)
    return JSON.stringify(a) === JSON.stringify(b)
}
